/**
 * featurebase/loader.ts
 * Idempotent, StrictMode-safe SDK script loader for Featurebase.
 *
 * Exports two functions:
 * - `loadFeaturebaseScript()` — injects the SDK <script> tag and resolves when loaded
 * - `removeFeaturebaseScript()` — best-effort removal of the script tag for teardown
 */

import { FEATUREBASE_SDK_URL } from './constants';

// ===== MODULE STATE =====

/** Selector for the injected script element */
const SCRIPT_SELECTOR = 'script[data-featurebase-sdk]';

/**
 * In-flight loading promise. Non-null while a load is in progress.
 * Ensures concurrent calls (e.g. React StrictMode double-invocation)
 * receive the same promise and don't create duplicate script tags.
 */
let loadingPromise: Promise<void> | null = null;

/**
 * Reject function for the in-flight loading promise.
 * Stored at module scope so `removeFeaturebaseScript()` can settle
 * the promise if removal happens while a load is pending.
 */
let pendingReject: ((reason: Error) => void) | null = null;

// ===== PUBLIC API =====

/**
 * Dynamically inject the Featurebase SDK script into the document head.
 *
 * Three-layer guard ensures idempotency and StrictMode safety:
 * 1. Module-level promise — deduplicates concurrent calls
 * 2. SDK global check — resolves immediately if already loaded
 * 3. DOM stale cleanup — removes failed/orphaned script tags before retry
 *
 * After script load, verifies `window.Featurebase` is defined.
 * If the SDK script downloaded but failed to initialize, the promise
 * rejects and module state resets so a retry is possible.
 *
 * @returns Promise that resolves when the SDK script has loaded and initialized
 */
export function loadFeaturebaseScript(): Promise<void> {
  // Guard 1 — concurrent dedup: return in-flight promise
  if (loadingPromise) {
    return loadingPromise;
  }

  // Guard 2 — SDK already loaded (covers HMR, page state reuse)
  if (window.Featurebase) {
    return Promise.resolve();
  }

  // Guard 3 — stale cleanup: remove orphaned/failed script tag
  const staleScript = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
  if (staleScript) {
    staleScript.remove();
  }

  // Create and inject script element
  const script = document.createElement('script');
  script.src = FEATUREBASE_SDK_URL;
  script.async = true;
  script.dataset['featurebaseSdk'] = 'true';

  loadingPromise = new Promise<void>((resolve, reject) => {
    pendingReject = reject;

    script.onload = (): void => {
      pendingReject = null;

      // Verify SDK actually initialized after script execution
      if (window.Featurebase) {
        resolve();
      } else {
        script.remove();
        loadingPromise = null;
        reject(
          new Error(
            'Featurebase SDK script loaded but window.Featurebase is not defined'
          )
        );
      }
    };

    script.onerror = (): void => {
      pendingReject = null;
      // Clean up failed script so retries start fresh
      script.remove();
      loadingPromise = null;
      reject(new Error('Failed to load Featurebase SDK script'));
    };
  });

  document.head.appendChild(script);

  return loadingPromise;
}

/**
 * Best-effort removal of the Featurebase SDK script tag from the DOM.
 *
 * If a load is in-flight, the pending promise is rejected with an abort
 * error and script event handlers are cleared to prevent late callbacks.
 *
 * Note: Removing the <script> tag does not unload the SDK from memory.
 * The SDK's `shutdown()` command (called by the service) is the proper
 * cleanup mechanism. This function handles cosmetic DOM cleanup and
 * resets module state so a future `loadFeaturebaseScript()` can re-inject.
 */
export function removeFeaturebaseScript(): void {
  const script = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);

  // Abort in-flight load: clear handlers to prevent late callbacks, then reject
  if (pendingReject) {
    if (script) {
      script.onload = null;
      script.onerror = null;
    }
    pendingReject(new Error('Featurebase SDK script load aborted by removal'));
    pendingReject = null;
  }

  if (script) {
    script.remove();
  }
  loadingPromise = null;
}
